import { useState } from "react";
import { Link } from "react-router-dom";
import { useAddInvoice } from "../hooks/useAddInvoice";
import { createInvoice } from "../services/invoiceAddService";

import InvoiceDetails from "../components/InvoiceDetails/InvoiceDetails";
import FilialDetails from "../components/FilialDetails/FilialDetails";
import InvoiceFooter from "../components/InvoiceFooter";
import SendEmailModal from "../components/SendEmailModal";

import styles from "../styles/addInvoice.module.css";

export default function AddInvoice() {
  const {
    invoiceData,
    updateField,
    resetFields,
    errors,
    validate,
    clearErrors,
  } = useAddInvoice();

  // isAgua se sube desde InvoiceDetailsBody para saber si validar medidas
  const [isAgua, setIsAgua] = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [apiError, setApiError] = useState("");

  // Estado para el correo
  const [lastCreated, setLastCreated] = useState(null); // factura recién creada
  const [emailModal, setEmailModal] = useState(null); // array de facturas | null

  async function handleSubmit(e) {
    e.preventDefault();
    setApiError("");
    setLastCreated(null);

    // 1. Validar campos antes de enviar
    const valid = validate(isAgua);
    if (!valid) return;

    setSaving(true);
    try {
      const created = await createInvoice(invoiceData);
      setLastCreated(created); // guardar para ofrecer enviar correo
      clearErrors();
      resetFields();
    } catch (err) {
      setApiError(err.message ?? "Ocurrió un error al guardar la factura.");
    } finally {
      setSaving(false);
    }
  }

  const errorCount = Object.keys(errors).length;

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Inicio /</span>
        </Link>
        <Link to="/finanzas/cxc">
          <span className={styles.inactive}> Cuentas por Cobrar /</span>
        </Link>
        <Link to="/finanzas/facturas">
          <span className={styles.inactive}> Facturas / </span>
        </Link>
         <span className={styles.active}>Agregar</span>
      </span>


      <h2 className="mt-2 mb-2 p-0 title">Generar Factura</h2>

      {/* ── Banner de éxito con opción de correo ──────────────────── */}
      {lastCreated && (
        <div
          style={{
            background: "rgba(74,222,128,0.08)",
            border: "1px solid rgba(74,222,128,0.3)",
            borderLeft: "4px solid #4ade80",
            borderRadius: 8,
            padding: "12px 18px",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <span
            style={{ color: "#4ade80", fontWeight: 500, fontSize: "0.9rem" }}
          >
            <i
              className="fa-solid fa-circle-check"
              style={{ marginRight: 8 }}
            />
            Factura {lastCreated.numero_factura} creada correctamente.
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setEmailModal([lastCreated])}
              style={{
                padding: "6px 14px",
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.4)",
                borderRadius: 7,
                color: "#f97316",
                fontSize: "0.83rem",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <i className="fa-solid fa-envelope" />
              Enviar correo al residente
            </button>
            <button
              onClick={() => setLastCreated(null)}
              style={{
                background: "none",
                border: "none",
                color: "#4ade80",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Resumen de errores de validación ──────────────────────── */}
      {errorCount > 0 && (
        <div
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.35)",
            borderLeft: "4px solid #ef4444",
            borderRadius: 8,
            padding: "12px 18px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#f87171",
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            <i className="fa-solid fa-triangle-exclamation" />
            Hay {errorCount} campo{errorCount > 1 ? "s" : ""} con error
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 20,
              color: "#fca5a5",
              fontSize: "0.83rem",
            }}
          >
            {Object.values(errors).map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Error de API ───────────────────────────────────────────── */}
      {apiError && (
        <div
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.35)",
            borderLeft: "4px solid #ef4444",
            borderRadius: 8,
            padding: "12px 18px",
            marginBottom: 16,
            color: "#f87171",
            fontSize: "0.88rem",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <i className="fa-solid fa-circle-exclamation" />
          {apiError}
        </div>
      )}

      {/* ── Modal de correo ────────────────────────────────────────── */}
      {emailModal && (
        <SendEmailModal
          facturas={emailModal}
          onClose={() => {
            setEmailModal(null);
            setLastCreated(null);
          }}
        />
      )}

      <div className={styles["invoice-container"]}>
        <InvoiceDetails
          invoiceData={invoiceData}
          updateField={updateField}
          errors={errors}
          onIsAguaChange={setIsAgua}
          isAgua={isAgua}
        />
        <FilialDetails
          invoiceData={invoiceData}
          updateField={updateField}
          errors={errors}
        />
        <InvoiceFooter
          onSubmit={handleSubmit}
          resetFields={() => {
            resetFields();
            clearErrors();
            setApiError("");
            setLastCreated(null);
          }}
          saving={saving}
        />
      </div>
    </>
  );
}
